/* ═══════════════════════════════════════════════════════════════
   OrgChartViewService — CRUD for saved org-chart view configurations
   (migration 039, People Rebuild edit-session grant, 2026-08-17).

   A "view" is a named bundle of DISPLAY preferences only (node shape/
   density/badges, routing, team-box visibility, snap) — never chart
   DATA (positions/edges/reportsTo stay in team_members/org_chart_edges/
   org_chart_junctions). `config` is opaque to the server; `visibility`
   is data-driven gating enforced server-side (ec2-api/server.js
   filterOrgChartViews) — list/query already returns only the rows this
   user is allowed to see, so no client-side filtering is needed here.

   Save/delete rules (enforced server-side, not duplicated here):
     - Creating a new view requires the same edit gate as the chart itself.
     - Updating or deleting an EXISTING view additionally requires being
       its creator or an admin (site_admin / admin_center.manage_roles) —
       a plain editor cannot repoint or delete a colleague's saved view.
   ═══════════════════════════════════════════════════════════════ */

import * as cache from './cloudCache';
import type { OrgChartView } from '../../shared/entities';

// `config` and `visibility` are the only jsonb fields; everything else is
// a scalar column. Declared here for the same reason org_chart_edges
// declares `waypoints` — keeps cloudCache from double-stringifying and
// lets a read trip back to plain JS objects without manual parsing.
const TABLE_OPTS: cache.TableOpts = { jsonFields: ['config', 'visibility'] };

export const OrgChartViewService = {
  list: async (): Promise<OrgChartView[]> =>
    cache.list<OrgChartView>('org_chart_views', TABLE_OPTS),

  save: async (view: OrgChartView): Promise<OrgChartView> =>
    cache.save<OrgChartView>('org_chart_views', view, TABLE_OPTS),

  delete: async (id: string): Promise<void> => cache.remove('org_chart_views', id),
};
